// Azure Text-to-Speech 服务集成
import { API_CONFIG } from './apiConfig.js'

export class AzureTtsService {
  constructor() {
    this.endpoint = API_CONFIG.api.tts
    this.debugLogs = API_CONFIG.debug.enableLogs
  }

  /**
   * 调用 /api/tts 生成语音（服务端负责生成 SSML 并调用 Azure）
   * @param {Object} payload - { text, voice, emotion, rate, pitch }
   * @returns {Promise<ArrayBuffer>} - 音频数据
   */
  async synthesizeSpeech(payload) {
    try {
      if (this.debugLogs) {
        console.log('🎤 开始调用TTS接口...', { voice: payload.voice, chars: payload.text.length })
      }

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`TTS API错误 ${response.status}: ${errorData.error || 'Unknown error'}`)
      }

      const audioData = await response.arrayBuffer()

      if (this.debugLogs) {
        console.log(`✅ TTS成功，音频大小: ${(audioData.byteLength / 1024).toFixed(1)}KB`)
      }

      return audioData

    } catch (error) {
      console.error('❌ TTS服务调用失败:', error)
      throw error
    }
  }

  /**
   * 播放音频数据
   * @param {ArrayBuffer} audioData - 音频数据
   * @param {HTMLAudioElement} audioElement - 音频元素
   * @returns {Promise} - 播放完成的Promise
   */
  async playAudio(audioData, audioElement) {
    return new Promise((resolve, reject) => {
      try {
        // 创建Blob URL
        const audioBlob = new Blob([audioData], { type: 'audio/mpeg' })
        const audioUrl = URL.createObjectURL(audioBlob)

        // 设置音频源并播放
        audioElement.src = audioUrl
        audioElement.load()

        // 监听播放完成事件
        audioElement.onended = () => {
          URL.revokeObjectURL(audioUrl) // 清理内存
          if (this.debugLogs) {
            console.log('🔊 音频播放完成')
          }
          resolve()
        }

        audioElement.onerror = (error) => {
          URL.revokeObjectURL(audioUrl)
          console.error('❌ 音频播放失败:', error)
          reject(error)
        }

        // 开始播放
        audioElement.play().catch(reject)

        if (this.debugLogs) {
          console.log('🔊 开始播放音频...')
        }

      } catch (error) {
        console.error('❌ 音频播放设置失败:', error)
        reject(error)
      }
    })
  }

  /**
   * 完整的TTS流程：文本 -> 服务端合成 -> 播放
   * @param {string} text - 要转换的文本
   * @param {HTMLAudioElement} audioElement - 音频元素
   * @param {Object} options - 配置选项
   */
  async synthesizeAndPlay(text, audioElement, options = {}) {
    const {
      voice = 'zh-HK-HiuMaanNeural',
      emotion = 'friendly',
      rate = 1.0,
      pitch = '0%'
    } = options

    try {
      if (this.debugLogs) {
        console.log('🎙️ 开始完整TTS流程:', {
          text: text.substring(0, 50) + '...',
          voice,
          emotion,
          rate,
          pitch
        })
      }

      // 1. 调用TTS接口（SSML 由服务端生成）
      const audioData = await this.synthesizeSpeech({ text, voice, emotion, rate, pitch })

      // 2. 播放音频
      await this.playAudio(audioData, audioElement)

      return { success: true, audioSize: audioData.byteLength }

    } catch (error) {
      console.error('❌ TTS完整流程失败:', error)
      throw error
    }
  }

  /**
   * 获取可用的语音列表
   */
  getAvailableVoices() {
    return API_CONFIG.voices
  }

  /**
   * 验证TTS服务配置（前端无密钥；服务端配置状态用 checkServerHealth() 查询）
   */
  validateConfiguration() {
    if (!this.endpoint) {
      throw new Error('TTS Endpoint配置缺失')
    }
    return true
  }
}

// 创建单例实例
export const azureTtsService = new AzureTtsService()